import '../style.scss';
import { useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { IEditReportState, IMetadataModalityTab, ISuccessFactorsTab, IResultsAndBenefitsTab } from '../models/IEditReportState';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestion } from '@fortawesome/free-solid-svg-icons';
import Popover from 'react-bootstrap/Popover';
import 'bootstrap/dist/css/bootstrap.min.css';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import QueryService from '../../../../shared/queryService';
import ConfigService from '../../../../shared/configService';
import { IRefModalityFeature, IRefFocalPointFeature } from '../models/IRefFeature';
import { IFeature } from '@esri/arcgis-rest-types';
import Utils from '../../../../shared/utils';
import Autocomplete from '../../../common/autocomplete/Autocomplete';

const ModalitiesTab = (props: {state: IEditReportState, setState: Function, validateData: Function, validateSuccessFactorsData: Function, validateResultsAndBenefitsTab: Function}) => {
  const {t} = useTranslation('reports');

  //Popup buttons
  let reportTypePopoverBtn: HTMLButtonElement;
  let successFactorsPopoverBtn: HTMLButtonElement;

  const reportTypePopover = (
    <Popover id="reportTypePopover">
      <div>
        <div dangerouslySetInnerHTML={{__html: props.state && props.state.metadataModalityTab && props.state.metadataModalityTab.report_type === ConfigService.getConfig().agol.referenceTables.REF_MODALITY.snapshotValue ? 
        t('newReport.metadataModality.reportTypeSnapshotExplanation') : 
        t('newReport.metadataModality.reportTypeStandardExplanation')}}>
        </div>
        <div className="popoverActionsDiv">
          <button onClick={() => togglePopup1()}>{t('newReport.metadataModality.gotIt')}</button>
        </div>
      </div>
    </Popover>
  );

  const successFactorsPopover = (
    <Popover id="successFactorsPopover">
      <div>
        <div dangerouslySetInnerHTML={{__html: t('newReport.metadataModality.successFactorExplanation')}}></div>
        <div className="popoverActionsDiv">
          <button onClick={() => togglePopup2()}>{t('newReport.metadataModality.gotIt')}</button>
        </div>
      </div>
    </Popover>
  );

  //Popups toggle
  const togglePopup1 = () => {
    if(reportTypePopoverBtn) {
      reportTypePopoverBtn.click();
    }
  }

  const togglePopup2 = () => {
    if(successFactorsPopoverBtn) {
      successFactorsPopoverBtn.click();
    }
  }

  // State
  interface ILocalState {
    reportLayerIndex: number;
    refModalityFeatures: IRefModalityFeature[];
    refFocalPointFeatures: IRefFocalPointFeature[];
  }
  const [localState, setTheLocalState] = useState<ILocalState>({
    reportLayerIndex: ConfigService.getConfig().agol?.operationalLayers.reportMain.index,
    refModalityFeatures: [],
    refFocalPointFeatures: []
  });

  // Use effect
  useEffect(() => {    
    //Title
    const selectBenefitsSpan: HTMLSpanElement | null = document.getElementById('selectBenefitsSpan');
    
    if(selectBenefitsSpan) {
      selectBenefitsSpan.innerHTML = t('newReport.metadataModality.selectSuccessFactor');
    }

    //Reference table
    getRefFeatures();

    // Set report parts
    setReportPartsState();
  }, []);

  /**
   * Set report parts state
   */
  const setReportPartsState = async() => {
    if(props.state) {
      const obj: IEditReportState = { ...props.state };
      const metaTab: IMetadataModalityTab = { ...obj.metadataModalityTab } as IMetadataModalityTab;    
      const reportParts: string | undefined = metaTab.report_parts;

      metaTab.successFactorPoliciesInstitOn = reportParts && reportParts.split(',').indexOf('1') != -1 ? true : false;
      metaTab.successFactorFinancialFlowOn = reportParts && reportParts.split(',').indexOf('2') != -1 ? true : false;
      metaTab.successFactorTechPlanningOn = reportParts && reportParts.split(',').indexOf('3') != -1 ? true : false;
      metaTab.successFactorMonitoringOn = reportParts && reportParts.split(',').indexOf('4') != -1 ? true : false;
      metaTab.benefitsHectaresRestorOn = true;
      metaTab.benefitsClimateImpactOn = reportParts && reportParts.split(',').indexOf('6') != -1 ? true : false;
      metaTab.benefitsBiodivImpactOn = reportParts && reportParts.split(',').indexOf('7') != -1 ? true : false;
      metaTab.benefitsSocioEcoImpactOn = reportParts && reportParts.split(',').indexOf('8') != -1 ? true : false;

      obj.metadataModalityTab = metaTab;
      await props.setState(obj);
    }
  }

  /**
   * Set local state
   * @param prop
   * @param value 
   */
  const setLocalState = (prop: string, value: any) => {
    const obj: ILocalState = {...localState};
    (obj as any)[prop] = value;
    setTheLocalState(obj);   
  }

  /**
   * Set parent state
   * @param prop
   * @param value 
   */
   const setParentState = (prop: string, value: any) => {

    if(props.state) {
      const obj: IEditReportState = {...props.state};
      const metaTab: IMetadataModalityTab = { ...obj.metadataModalityTab } as IMetadataModalityTab;
      const sfTab: ISuccessFactorsTab = { ...obj.successFactorsTab } as ISuccessFactorsTab;
      const rbTab: IResultsAndBenefitsTab = { ...obj.resultsAndBenefitsTab } as IResultsAndBenefitsTab;

      (metaTab as any)[prop] = value;      

      // Check isOK
      props.validateData(metaTab);

      // Report parts
      metaTab.report_parts = getReportParts(metaTab);
      
      obj.metadataModalityTab = metaTab;
      obj.successFactorsTab = sfTab;
      obj.resultsAndBenefitsTab = rbTab;      

      props.validateSuccessFactorsData(sfTab, metaTab);
      props.validateResultsAndBenefitsTab(rbTab, metaTab); 
      
      props.setState(obj);
    }
  }

  /**
   * Set report type
   * @param type 
   */
  const setReportType = (type: string | undefined) => {
    if(type) {
      setParentState('report_type', type);
    }
  }

  /**
   * Set success factor
   * @param index 
   */
  const setSuccessFactor = (index: number) => {
    switch(index) {
      case 0:
        setParentState('successFactorPoliciesInstitOn', !props.state.metadataModalityTab?.successFactorPoliciesInstitOn);
      break;

      case 1:
        setParentState('successFactorFinancialFlowOn', !props.state.metadataModalityTab?.successFactorFinancialFlowOn);
      break;
      
      case 2:
        setParentState('successFactorTechPlanningOn', !props.state.metadataModalityTab?.successFactorTechPlanningOn);
      break;
      
      case 3:
        setParentState('successFactorMonitoringOn', !props.state.metadataModalityTab?.successFactorMonitoringOn);
      break;
    }
  }

  /**
   * Set benefits
   * @param index 
   */
   const setBenefits = (index: number) => {
    switch(index) {
      case 0:
        setParentState('benefitsHectaresRestorOn', !props.state.metadataModalityTab?.benefitsHectaresRestorOn);
      break;

      case 1:
        setParentState('benefitsClimateImpactOn', !props.state.metadataModalityTab?.benefitsClimateImpactOn);
      break;
      
      case 2:
        setParentState('benefitsBiodivImpactOn', !props.state.metadataModalityTab?.benefitsBiodivImpactOn);
      break;
      
      case 3:
        setParentState('benefitsSocioEcoImpactOn', !props.state.metadataModalityTab?.benefitsSocioEcoImpactOn);
      break;
    }
  }

  /**
   * Get report parts
   * @param tab 
   * @returns 
   */
  const getReportParts = (tab: IMetadataModalityTab) => {
    let parts: number[] = [];
    
    if (tab?.successFactorPoliciesInstitOn) {
      parts.push(1);
    }

    if (tab?.successFactorFinancialFlowOn) {
      parts.push(2);
    }

    if (tab?.successFactorTechPlanningOn) {
      parts.push(3);
    }

    if (tab?.successFactorMonitoringOn) {
      parts.push(4);
    }

    if (tab?.benefitsHectaresRestorOn) {
      parts.push(5);
    }

    if (tab?.benefitsClimateImpactOn) {
      parts.push(6);
    }

    if (tab?.benefitsBiodivImpactOn) {
      parts.push(7);
    }

    if (tab?.benefitsSocioEcoImpactOn) {
      parts.push(8);
    }

    return parts.join(',');
  }

  /**
   * Handle FocalPoint change
   * @param selectedPoint 
   */
  const onFocalPointEmailChange = (selectedPoint: any) => {
    const focalNumber: string = selectedPoint[ConfigService.getConfig().agol?.referenceTables.REF_IUCN_FOCAL_POINT_EMAIL.fields.key];

    setParentState('focal_point_email', focalNumber);    
  }

  /**
   * Handle FocalPoint clear
   */
  const onFocalPointEmailClear = () => {
    setParentState('focal_point_email', null);
  }
  
  /**
   * Get selected FocalPoint
   * @param selectedPoint 
   */
  const setSelectedFocalPointEmail = (selected: IRefFocalPointFeature): void => {
    const evt = new CustomEvent("SetAutocompleteValue", {detail: selected});
    window.dispatchEvent(evt);
  }

  /**
   * Get reference list
   */
   const getRefFeatures = async () => {      
    const modalityFeatures: IFeature[] = await QueryService.queryReferenceFeatures(ConfigService.getConfig().agol?.referenceTables.REF_MODALITY.index);
    const focalPointsFeatures: IFeature[] = await QueryService.queryReferenceFeatures(ConfigService.getConfig().agol?.referenceTables.REF_IUCN_FOCAL_POINT_EMAIL.index);

    const obj: ILocalState = { ...localState };
    obj.refModalityFeatures = modalityFeatures.map((feat: IFeature) => feat.attributes as IRefModalityFeature);
    obj.refFocalPointFeatures = focalPointsFeatures.map((feat: IFeature) => feat.attributes as IRefFocalPointFeature);
    
    // Set selected focal email
    const selected: IRefFocalPointFeature | undefined = obj.refFocalPointFeatures.find((i:IRefFocalPointFeature) => i.key_ === props.state.metadataModalityTab?.focal_point_email);
    if(selected) {
      setSelectedFocalPointEmail(selected);
    }

    setTheLocalState(obj);

    // Validate data
    setValidState();
  };

  /**
   * Set valid state
   */
  const setValidState = () => {
    const obj2: IEditReportState = {...props.state};
    const subObj: IMetadataModalityTab = { ...obj2.metadataModalityTab } as IMetadataModalityTab;
    
    if(subObj) {
      props.validateData(subObj);
    }

    obj2.metadataModalityTab = subObj;
    props.setState(obj2);
  }

  /**
   * Check if a field is required
   * @param field
   */
  const isRequired = (field: string): boolean => {
    return Utils.isRequiredField(ConfigService.getConfig().agol.operationalLayers.reportMain.requiredFields.metadataModality, field);
  }

  return(
    <div className="oneTabContent metadataTab">
      <span className="bigText">{t('newReport.metadataModality.label')}</span>

      <div className="separtorDiv"></div>

      <div className="row">            
        <span className="titleSpan mediumText">{t('newReport.metadataModality.reportingCountry')}</span>                       
        <input className="mediumText medium" disabled type="text" placeholder={t('newReport.metadataModality.reportingCountryPlaceholder')} value={props.state.metadataModalityTab?.reportingCountry}/>  
      </div>

      <div className="row">            
        <span className="titleSpan mediumText">{t('newReport.metadataModality.reportingState')}</span>                       
        <input className="mediumText medium" disabled type="text" placeholder={t('newReport.metadataModality.reportingStatePlaceholder')} value={props.state.metadataModalityTab?.reportingState}/>  
      </div>

      <div className="row">            
        <span className="titleSpan mediumText">{t('newReport.metadataModality.reportingYear')}</span>                       
        <input className="mediumText verySmall" disabled type="text" placeholder={t('newReport.metadataModality.reportingYearPlaceholder')} value={props.state.metadataModalityTab?.rep_year}/>  
      </div>

      <div className="row">            
        <span className="titleSpan mediumText">{t('newReport.metadataModality.coordinatGovernmentInstit')}</span>                       
        <input className="mediumText big" type="text" required = {isRequired('coord_gvt')}
        maxLength={Utils.getFieldInfos(localState.reportLayerIndex, 'coord_gvt')?.length || 9999}
        placeholder={t('newReport.metadataModality.coordinatGovernmentInstitPlaceholder')} 
        value={props.state.metadataModalityTab?.coord_gvt} onChange={(e) => {setParentState('coord_gvt', e.target.value)}}/>  
      </div>

      <div className="row">            
        <span className="titleSpan mediumText">{t('newReport.metadataModality.focalPointName')}</span>                       
        <input className="mediumText medium" type="text" required = {isRequired('focal_point_name')}
        maxLength={Utils.getFieldInfos(localState.reportLayerIndex, 'focal_point_name')?.length || 9999}
        placeholder={t('newReport.metadataModality.focalPointNamePlaceholder')} 
        value={props.state.metadataModalityTab?.focal_point_name} onChange={(e) => {setParentState('focal_point_name', e.target.value)}}/>  
      </div>

      <div className="row">            
        <span className="titleSpan mediumText">{t('newReport.metadataModality.designation')}</span>                       
        <input className="mediumText big" type="text" required = {isRequired('designation')}
        maxLength={Utils.getFieldInfos(localState.reportLayerIndex, 'designation')?.length || 9999}
        placeholder={t('newReport.metadataModality.designationPlaceholder')} 
        value={props.state.metadataModalityTab?.designation} onChange={(e) => {setParentState('designation', e.target.value)}}/>  
      </div>

      <div className="row">            
        <span className="titleSpan mediumText">{t('newReport.metadataModality.email')}</span>                       
        <input className="mediumText big" type="text" required = {isRequired('email')}
        maxLength={Utils.getFieldInfos(localState.reportLayerIndex, 'email')?.length || 9999}
        placeholder={t('newReport.metadataModality.emailPlaceholder')}
        value={props.state.metadataModalityTab?.email} onChange={(e) => {setParentState('email', e.target.value)}}/>  
      </div>

      <div className="row">            
        <span className="titleSpan mediumText">{t('newReport.metadataModality.telephoneNumber')}</span>
        <div className={isRequired('phone') ? "required" : ""}>
          <PhoneInput
            placeholder={t('newReport.metadataModality.telephoneNumberPlaceholder')}
            country={props.state.metadataModalityTab?.countryCode}
            value={props.state.metadataModalityTab?.phone}
            onChange={(phone) => {setParentState('phone', phone)}}
          />
        </div>
      </div>

      <div className="row">            
        <span className="titleSpan mediumText">{t('newReport.metadataModality.iucnFocalPoint')}</span>                       
        {/* <input className="mediumText medium" type="text" placeholder={t('newReport.metadataModality.focalPointNamePlaceholder')} required value={props.state.metadataModalityTab?.focalPointName} onChange={(e) => {setParentState('focalPointName', e.target.value)}}/>   */}
        <div className={isRequired('focal_point_email') ? "required" : ""}>
          <Autocomplete
              className={isRequired('focal_point_email') ? "mediumText required" : "mediumText"}
              labelKey={ConfigService.getConfig().agol?.referenceTables.REF_IUCN_FOCAL_POINT_EMAIL.fields.value}
              placeholder={t('newReport.metadataModality.iucnFocalPointPlaceholder')}
              data={localState.refFocalPointFeatures}
              onChange={onFocalPointEmailChange}
              onClear={onFocalPointEmailClear}/>
        </div>
      </div>
      
      
      {localState.refModalityFeatures && localState.refModalityFeatures.length &&
      <div className="row">            
        <span className="titleSpan mediumText">
          {t('newReport.metadataModality.reportType')}
          <OverlayTrigger trigger="click" placement="right" rootClose overlay={reportTypePopover}>      
          <button className="questionIcon" ref={(btn: HTMLButtonElement) => reportTypePopoverBtn = btn }><FontAwesomeIcon icon={faQuestion}/></button>
          </OverlayTrigger>
        </span>

        {localState.refModalityFeatures.map((item:IRefModalityFeature, i: number) =>
          <div className={"choiceDiv " + (props.state.metadataModalityTab?.report_type === item.value && 'selected')} key={i} 
          onClick={() => setReportType(item.value)}>{item.value}
          </div>
        )}      
      </div>
      }

      { /*Visible only in standard mode */
      props.state && props.state.metadataModalityTab && props.state.metadataModalityTab.report_type === ConfigService.getConfig().agol.referenceTables.REF_MODALITY.standardValue &&
      <div className="row">            
        <span className="titleSpan mediumText">{t('newReport.metadataModality.participatoryValidation')}</span>                       
        <textarea className="mediumText big" rows={5} required = {isRequired('report_process')}
        maxLength={Utils.getFieldInfos(localState.reportLayerIndex, 'report_process')?.length || 9999}
        placeholder={t('newReport.metadataModality.participatoryValidationPlaceholder')} value={props.state.metadataModalityTab?.report_process} onChange={(e) => {setParentState('report_process', e.target.value)}}/>  
      </div>
      }
      
      { /*Visible only in snapshot mode */
      props.state && props.state.metadataModalityTab && props.state.metadataModalityTab.report_type === ConfigService.getConfig().agol.referenceTables.REF_MODALITY.snapshotValue &&
      <div>
        <div className="row">            
          <span id="selectBenefitsSpan" className="titleSpan mediumText"></span>

          <p>
            <div className={"choiceDiv " + (props.state.metadataModalityTab?.successFactorPoliciesInstitOn && 'selected')}
            onClick={() => setSuccessFactor(0)}>
              {t('newReport.metadataModality.policiesAndInstitutions')}
            </div>

            <div className={"choiceDiv " + (props.state.metadataModalityTab?.successFactorFinancialFlowOn && 'selected')}
            onClick={() => setSuccessFactor(1)}>
              {t('newReport.metadataModality.financialFlows')}
            </div>   
          </p>

          <p>
            <div className={"choiceDiv " + (props.state.metadataModalityTab?.successFactorTechPlanningOn && 'selected')}
            onClick={() => setSuccessFactor(2)}>
              {t('newReport.metadataModality.technicalPlanning')}
            </div>

            <div className={"choiceDiv " + (props.state.metadataModalityTab?.successFactorMonitoringOn && 'selected')}
            onClick={() => setSuccessFactor(3)}>
              {t('newReport.metadataModality.monitoring')}
            </div>
          </p>        
        </div>
     

        <div className="row">            
          <span className="titleSpan mediumText">
            {t('newReport.metadataModality.selectBenefits')}
            
            <OverlayTrigger trigger="click" placement="right" rootClose overlay={successFactorsPopover}>      
            <button className="questionIcon" ref={(btn: HTMLButtonElement) => successFactorsPopoverBtn = btn }><FontAwesomeIcon icon={faQuestion}/></button>
            </OverlayTrigger>
          </span>

          <p>
            <div className={"choiceDiv " + (props.state.metadataModalityTab?.benefitsHectaresRestorOn && 'selected')}>
              {t('newReport.metadataModality.hectaresUnderRestor')}
            </div>

            <div className={"choiceDiv " + (props.state.metadataModalityTab?.benefitsClimateImpactOn && 'selected')}
            onClick={() => setBenefits(1)}>
              {t('newReport.metadataModality.climateImpacts')}
            </div>   
          </p>

          <p>
            <div className={"choiceDiv " + (props.state.metadataModalityTab?.benefitsBiodivImpactOn && 'selected')}
            onClick={() => setBenefits(2)}>
              {t('newReport.metadataModality.biodiversityImpacts')}
            </div>

            <div className={"choiceDiv " + (props.state.metadataModalityTab?.benefitsSocioEcoImpactOn && 'selected')}
            onClick={() => setBenefits(3)}>
              {t('newReport.metadataModality.socioEconomicImpacts')}
            </div>
          </p>
        </div>
      </div>
      }

    </div>
  );
};

export default ModalitiesTab;

